import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { ActingBanner } from './components/ViewSwitcher';
import { NotificationProvider } from './NotificationContext';
import { ThemeProvider } from './ThemeContext';
import { Toaster } from './components/ui/sonner';
import OnboardingTour from './components/OnboardingTour';
// Eager-loaded: landing & auth are the entry points and we want them instant.
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
// Lazy-loaded: every other route is fetched on demand. This drops the initial
// JS bundle by ~60-70% (heaviest offenders are AdminDashboard, TutorDashboard,
// LessonPage which depend on chart/video libs).
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const TutorsPage = lazy(() => import('./pages/TutorsPage'));
const TutorProfilePage = lazy(() => import('./pages/TutorProfilePage'));
const StudentDashboard = lazy(() => import('./pages/StudentDashboard'));
const TutorDashboard = lazy(() => import('./pages/TutorDashboard'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const BookingPaymentPage = lazy(() => import('./pages/BookingPaymentPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const PaymentCancelPage = lazy(() => import('./pages/PaymentCancelPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const LessonPage = lazy(() => import('./pages/LessonPage'));
const WiseTransferPage = lazy(() => import('./pages/WiseTransferPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const EntryTestPage = lazy(() => import('./pages/EntryTestPage'));
const ApplyPage = lazy(() => import('./pages/ApplyPage'));
const PracticeClassroomPage = lazy(() => import('./pages/PracticeClassroomPage'));
const PlansPage = lazy(() => import('./pages/PlansPage'));
import InstallPWAPrompt from './components/InstallPWAPrompt';
import UpdateChecker from './components/UpdateChecker';
import MobileTableLabels from './components/MobileTableLabels';
import { MeshBackground } from './components/BrandDecor';
import './App.css';

// Lightweight, branded skeleton shown while a lazy route chunk is loading.
// Uses inline styles so it can render before any CSS chunk has finished.
const RouteSkeleton = () => (
  <div
    style={{
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: 12,
    }}
    data-testid="route-loading"
    role="status"
    aria-live="polite"
  >
    <div
      style={{
        width: 36,
        height: 36,
        border: '3px solid #e2e8f0',
        borderTopColor: '#1e40af',
        borderRadius: '50%',
        animation: 'route-spin 0.8s linear infinite',
      }}
    />
    <p style={{ color: '#64748b', fontSize: 14, margin: 0 }}>Loading…</p>
    <style>{`@keyframes route-spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Check payment flow flag
  const paymentInProgress = sessionStorage.getItem('payment_in_progress');
  
  // CRITICAL: If we have a token in localStorage, trust it during initial load
  // This handles the Stripe back button case where React re-renders
  const hasToken = !!localStorage.getItem('token');
  
  // If we have a token and loading is true, show loading spinner
  // The token will be validated soon by AuthContext
  if (loading && hasToken) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  // If loading with no token and no user, also show loading briefly
  if (loading && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // No authenticated user and no token - redirect to auth
  if (!user && !hasToken) {
    // Clear any stale payment flag
    if (paymentInProgress) {
      sessionStorage.removeItem('payment_in_progress');
    }
    // Remember where they were going (e.g. a class link from an email) so login
    // can send them straight back instead of to the dashboard.
    return <Navigate to="/auth" replace state={{ from: location.pathname + location.search }} />;
  }
  
  // If we have a token but no user yet (shouldn't happen with sync init, but safety check)
  if (!user && hasToken) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // User is authenticated - clear payment flag if exists
  if (paymentInProgress) {
    sessionStorage.removeItem('payment_in_progress');
  }

  // Applicants only have their application page until they get full access
  if (user.role === 'applicant') {
    return <Navigate to="/apply" replace />;
  }

  // Check role requirement
  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AnimatedRoutes() {
  const location = useLocation();
  const firstPageRef = useRef(location.pathname);
  useEffect(() => {
    if (firstPageRef.current !== location.pathname) firstPageRef.current = null;
  }, [location.pathname]);

  // Intercept Emergent Google OAuth callback BEFORE any Route matches so that
  // an existing session in AuthProvider doesn't race us. AuthCallback consumes
  // the session_id, upgrades to a JWT, then navigates to the dashboard.
  if (location.hash?.includes('session_id=') || location.hash?.includes('google_token=')) {
    return <AuthCallback />;
  }

  // Fade each new page in (CSS, no animation library). The first page after a
  // load shows straight away so it paints as early as possible.
  return (
      <div key={location.pathname} className={firstPageRef.current === location.pathname ? undefined : 'page-fade'}>
        <Routes location={location}>
        <Route path="/" element={<LandingPage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/tutors" element={<TutorsPage />} />
        <Route path="/tutors/:id" element={<TutorProfilePage />} />
        {/* Teacher applications: public sign-up + the applicant's own page */}
        <Route path="/apply" element={<ApplyPage />} />
        <Route
          path="/classroom/practice"
          element={
            <ProtectedRoute>
              <PracticeClassroomPage />
            </ProtectedRoute>
          }
        />
        
        {/* Protected Routes */}
        <Route
          path="/entry-test"
          element={
            <ProtectedRoute requiredRole="student">
              <EntryTestPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/student"
          element={
            <ProtectedRoute requiredRole="student">
              <StudentDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/tutor"
          element={
            <ProtectedRoute requiredRole="tutor">
              <TutorDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/admin"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/booking/:bookingId/payment"
          element={
            <ProtectedRoute requiredRole="student">
              <BookingPaymentPage />
            </ProtectedRoute>
          }
        />
        
        {/* Payment Routes */}
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/payment/cancel" element={<PaymentCancelPage />} />
        
        {/* Cart Route */}
        <Route
          path="/cart"
          element={
            <ProtectedRoute requiredRole="student">
              <CartPage />
            </ProtectedRoute>
          }
        />
        
        {/* Packages Route */}
        {/* Class plans replace the old session packages */}
        <Route path="/plans" element={<PlansPage />} />
        <Route path="/packages" element={<Navigate to="/plans" replace />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        
        {/* Notifications Route */}
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <NotificationsPage />
            </ProtectedRoute>
          }
        />
        
        {/* Lesson Video Call Route */}
        <Route
          path="/lesson/:bookingId"
          element={
            <ProtectedRoute>
              <LessonPage />
            </ProtectedRoute>
          }
        />
        
        <Route path="/pay/transfer/:externalId" element={<WiseTransferPage />} />
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
  );
}

function AppContent() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  return (
    <>
      <MeshBackground animated={pathname === '/'} />
      {/* Onboarding tour: shows once for new students after first login */}
      {/* Never interrupt a live class with the tour */}
      {!pathname.startsWith('/lesson') && <OnboardingTour user={user} />}
      <Suspense fallback={<RouteSkeleton />}>
        <AnimatedRoutes />
      </Suspense>
      <Toaster position="top-right" richColors closeButton />
      <MobileTableLabels />
      <UpdateChecker />
      {/* Super admin using the site as a teacher/student: way back to admin */}
      {!pathname.startsWith('/lesson') && <ActingBanner />}
      <InstallPWAPrompt />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <NotificationProvider>
            <AppContent />
          </NotificationProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
